import { useState, useMemo } from "react";
import {
  menuCategories,
  allergenLegend,
  allergenDisclaimer,
} from "@/data/menu";

function AllergenBadges({ codes }) {
  if (!codes || codes.length === 0) return null;
  return (
    <span
      data-testid="allergen-badges"
      className="ml-2 inline-flex flex-wrap items-baseline gap-1 align-baseline"
    >
      {codes.map((code) => (
        <span
          key={code}
          title={allergenLegend[code] || code}
          className="menu-allergen-badge"
        >
          {code}
        </span>
      ))}
    </span>
  );
}

export default function MenuSection() {
  const [activeId, setActiveId] = useState(menuCategories[0].id);
  const active = menuCategories.find((c) => c.id === activeId);

  const usedCodes = useMemo(() => {
    const set = new Set();
    active.items.forEach((it) => {
      (it.allergens || []).forEach((c) => set.add(c));
      (it.toppingGroups || []).forEach((g) =>
        g.items.forEach((t) => (t.allergens || []).forEach((c) => set.add(c)))
      );
    });
    return Array.from(set).sort();
  }, [active]);

  return (
    <section id="menu" data-testid="menu-section" className="menu-pink relative">
      <div className="scallop-top" aria-hidden="true" />

      <div className="mx-auto max-w-5xl px-6 md:px-10 pt-10 pb-16 md:pt-16 md:pb-24">
        {/* Heading */}
        <div className="text-center mb-12">
          <span className="menu-eyebrow">Menu · Made to Crave</span>
          <h2
            data-testid="menu-heading"
            className="menu-pink-display mt-4 text-5xl md:text-6xl font-extrabold uppercase tracking-tight"
            style={{ color: "#2a1a1f" }}
          >
            Scoops&Co
          </h2>
          <p className="mt-3 text-sm md:text-base" style={{ color: "#5c3645" }}>
            handcrafted · ceremonial matcha · homemade cookies
          </p>
        </div>

        {/* Category Tabs */}
        <div
          data-testid="menu-categories"
          className="mb-12 flex flex-wrap justify-center gap-2"
        >
          {menuCategories.map((cat) => (
            <button
              key={cat.id}
              data-testid={`menu-category-${cat.id}`}
              onClick={() => setActiveId(cat.id)}
              data-active={activeId === cat.id ? "true" : "false"}
              className="menu-tab"
            >
              {cat.title}
            </button>
          ))}
        </div>

        {/* Active category */}
        <div
          key={active.id}
          data-testid={`menu-content-${active.id}`}
          className="animate-fade-up"
        >
          {/* Category title */}
          <div className="text-center mb-10">
            <h3
              className="menu-pink-display text-4xl md:text-5xl font-extrabold uppercase tracking-tight"
              style={{ color: "#2a1a1f" }}
            >
              {active.title}
            </h3>
            <p
              className="mt-3 text-sm md:text-base italic"
              style={{ color: "#5c3645" }}
            >
              {active.tagline}
            </p>
            {active.drinkNote && (
              <p
                className="mt-3 text-xs leading-relaxed max-w-xl mx-auto"
                style={{ color: "#7a4859" }}
              >
                ℹ {active.drinkNote}
              </p>
            )}
          </div>

          {/* Items list */}
          {active.items.length === 0 ? (
            <div className="menu-legend-card p-8 text-center">
              <p
                className="menu-eyebrow"
                style={{ color: "#c0427a" }}
              >
                Coming Soon
              </p>
              <p
                className="mt-3 text-base md:text-lg"
                style={{ color: "#5c3645" }}
              >
                Aktuell noch kein Special aktiv – schau bald wieder rein.
              </p>
            </div>
          ) : (
          <ul className="space-y-1">
            {active.items.map((item, idx) => (
              <li
                key={`${active.id}-${idx}`}
                data-testid={`menu-item-${active.id}-${idx}`}
                className="py-3"
              >
                <div className="flex items-baseline gap-3">
                  <span className="menu-bullet mt-2.5" aria-hidden="true" />
                  <div className="flex flex-1 flex-wrap items-baseline">
                    <span
                      className="menu-pink-display text-lg md:text-xl font-semibold"
                      style={{ color: "#2a1a1f" }}
                    >
                      {item.name}
                    </span>
                    <AllergenBadges codes={item.allergens} />
                    <span className="menu-dotted-line hidden md:block" />
                    {item.price && (
                      <span
                        className="ml-auto md:ml-0 menu-pink-display text-base md:text-lg font-bold whitespace-nowrap"
                        style={{ color: "#2a1a1f" }}
                      >
                        {item.price}
                      </span>
                    )}
                  </div>
                </div>
                {item.description && (
                  <p
                    className="ml-5 mt-1 text-xs md:text-sm leading-relaxed"
                    style={{ color: "#5c3645" }}
                  >
                    {item.description}
                  </p>
                )}

                {item.toppingGroups && (
                  <div
                    data-testid={`topping-groups-${active.id}-${idx}`}
                    className="menu-topping-card mt-5 ml-5 p-5 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-5"
                  >
                    {item.toppingGroups.map((group) => (
                      <div key={group.label}>
                        <p
                          className="menu-eyebrow"
                          style={{ color: "#c0427a" }}
                        >
                          {group.label}
                        </p>
                        <ul className="mt-2 space-y-1">
                          {group.items.map((t) => (
                            <li
                              key={t.name}
                              className="flex items-baseline justify-between gap-2 text-sm"
                              style={{ color: "#2a1a1f" }}
                            >
                              <span className="flex items-baseline gap-2">
                                <span
                                  className="menu-bullet mt-1.5"
                                  style={{
                                    width: 5,
                                    height: 5,
                                    backgroundColor: "#c0427a",
                                  }}
                                  aria-hidden="true"
                                />
                                {t.name}
                              </span>
                              <AllergenBadges codes={t.allergens} />
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          )}

          {/* Allergen Legend */}
          {usedCodes.length > 0 && (
            <div
              data-testid="allergen-legend"
              className="menu-legend-card mt-12 p-6"
            >
              <p className="menu-eyebrow">Allergen-Legende</p>
              <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {usedCodes.map((code) => (
                  <li
                    key={code}
                    className="flex items-baseline gap-3 text-sm"
                    style={{ color: "#2a1a1f" }}
                  >
                    <span className="menu-allergen-badge">{code}</span>
                    <span>{allergenLegend[code]}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Disclaimer */}
        <div className="mt-12 text-center max-w-2xl mx-auto">
          <p
            className="menu-eyebrow inline-block"
            style={{ color: "#c0427a" }}
          >
            Allergen-Hinweis
          </p>
          <p
            data-testid="allergen-disclaimer"
            className="mt-2 text-xs md:text-sm leading-relaxed"
            style={{ color: "#5c3645" }}
          >
            {allergenDisclaimer}
          </p>
          <p
            className="mt-3 text-[10px] uppercase tracking-[0.2em]"
            style={{ color: "#8a5567" }}
          >
            Änderungen vorbehalten.
          </p>
        </div>
      </div>

      <div className="scallop-bottom" aria-hidden="true" />
    </section>
  );
}
